import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Globe, Sparkles } from 'lucide-react';

/**
 * Terms & Conditions — full standalone page.
 * Routed at /terms ; linked from signup, landing footer, and Help menu.
 *
 * Same content can be rendered inline (see <TermsContent />) by the
 * in-app Help modal so users don't have to leave the editor.
 */
export default function TermsPage() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-cyan-300 transition-colors"
            data-testid="terms-back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="text-xs text-zinc-500 hidden sm:block">AV Simulation Tool</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <TermsContent />

        <div className="mt-12 pt-6 border-t border-zinc-800 text-center text-xs text-zinc-500">
          <Link to="/" className="hover:text-cyan-300 transition-colors">← Return to AV Simulation Tool</Link>
        </div>
      </main>
    </div>
  );
}

// ── Reusable content block (used by /terms page and Help modal) ────────
export function TermsContent() {
  return (
    <article className="space-y-7" data-testid="terms-content">
      {/* Title */}
      <header>
        <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-cyan-300">
          <Sparkles className="w-3 h-3" /> Legal
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-white tracking-tight">
          Terms &amp; Conditions
        </h1>
        <p className="mt-2 text-sm text-zinc-400">
          AV Simulation Tool — Powered by Ideation Audio Visual
        </p>
      </header>

      <Section number="1" title="Introduction">
        Welcome to <strong className="text-white">AV Simulation Tool</strong>, a browser-based platform developed by
        Ideation Audio Visual. By accessing or using this application, you agree to comply with and be bound by
        the following Terms &amp; Conditions.
      </Section>

      <Section number="2" title="Nature of the Service">
        AV Simulation Tool is a web-based simulation platform designed to assist users in creating and visualizing
        projection, LED, and AV setups.
        <ul className="mt-2 list-disc pl-5 space-y-1 text-zinc-300">
          <li>The application runs within your browser environment</li>
          <li>Performance may depend on your device, browser, and internet connection</li>
          <li>The tool is provided &ldquo;as is&rdquo; without guaranteed uninterrupted performance</li>
        </ul>
      </Section>

      <Section number="3" title="User Responsibility for Data">
        <ul className="list-disc pl-5 space-y-1 text-zinc-300">
          <li>Users are fully responsible for saving, exporting, and backing up their own project data</li>
          <li>AV Simulation Tool does not guarantee permanent storage or recovery of user projects</li>
          <li>Due to the browser-based nature of the platform, data may be affected by:
            <ul className="list-[circle] pl-5 mt-1 space-y-0.5 text-zinc-400">
              <li>Browser crashes</li>
              <li>System performance issues</li>
              <li>Internet interruptions</li>
              <li>Device limitations</li>
            </ul>
          </li>
        </ul>
        <Callout>
          Ideation Audio Visual is not responsible for any loss of data, project files, or work progress under any
          circumstances.
        </Callout>
      </Section>

      <Section number="4" title="Performance Disclaimer">
        <ul className="list-disc pl-5 space-y-1 text-zinc-300">
          <li>The application relies on real-time browser rendering technologies</li>
          <li>Performance may vary depending on:
            <ul className="list-[circle] pl-5 mt-1 space-y-0.5 text-zinc-400">
              <li>Hardware capability</li>
              <li>GPU support</li>
              <li>Browser settings</li>
              <li>Internet speed</li>
            </ul>
          </li>
        </ul>
        <Callout>
          The system may occasionally slow down, lag, or fail to load or save data. Ideation Audio Visual shall not
          be held responsible for any such performance-related issues or their impact on user work.
        </Callout>
      </Section>

      <Section number="5" title="Privacy & Data Security">
        We respect user privacy and maintain a secure environment.
        <ul className="mt-2 list-disc pl-5 space-y-1 text-zinc-300">
          <li>We do <strong className="text-white">NOT</strong> access, view, or analyze your project data or designs</li>
          <li>Your work remains private and under your control</li>
        </ul>
        <p className="mt-3">We only collect minimal information such as:</p>
        <ul className="list-disc pl-5 space-y-1 text-zinc-300">
          <li>Name</li>
          <li>Email address</li>
        </ul>
        <p className="mt-3">This data is used strictly for:</p>
        <ul className="list-disc pl-5 space-y-1 text-zinc-300">
          <li>System performance improvements</li>
          <li>User management and support</li>
          <li>Platform analytics and optimization</li>
        </ul>
        <Callout>Your project data is not used, shared, or accessed by us.</Callout>
      </Section>

      <Section number="6" title="User Conduct">
        Users agree to:
        <ul className="mt-2 list-disc pl-5 space-y-1 text-zinc-300">
          <li>Use the platform for lawful and professional purposes</li>
          <li>Not attempt to disrupt, misuse, or overload the system</li>
          <li>Not exploit or reverse-engineer the application</li>
        </ul>
      </Section>

      <Section number="7" title="Limitation of Liability">
        Under no circumstances shall Ideation Audio Visual be liable for:
        <ul className="mt-2 list-disc pl-5 space-y-1 text-zinc-300">
          <li>Loss of data</li>
          <li>Loss of projects</li>
          <li>Business interruptions</li>
          <li>Financial or operational losses</li>
          <li>Any indirect or consequential damages</li>
        </ul>
      </Section>

      <Section number="8" title="Updates & Changes">
        We may update or improve the platform at any time without prior notice. These updates may affect features,
        performance, or availability.
      </Section>

      <Section number="9" title="Acceptance of Terms">
        By using AV Simulation Tool, you acknowledge that:
        <ul className="mt-2 list-disc pl-5 space-y-1 text-zinc-300">
          <li>You understand the browser-based limitations</li>
          <li>You accept responsibility for your data</li>
          <li>You agree to these Terms &amp; Conditions</li>
        </ul>
      </Section>

      <Section number="10" title="Contact">
        For any questions or support:
        <div className="mt-3 flex flex-col sm:flex-row gap-3 text-sm">
          <a
            href="mailto:admin@ideationav.com"
            className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 transition-colors"
            data-testid="terms-contact-email"
          >
            <Mail className="w-4 h-4" /> admin@ideationav.com
          </a>
          <a
            href="https://www.ideationav.com"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 transition-colors"
            data-testid="terms-contact-web"
          >
            <Globe className="w-4 h-4" /> www.ideationav.com
          </a>
        </div>
      </Section>
    </article>
  );
}

const Section = ({ number, title, children }) => (
  <section>
    <h2 className="text-base sm:text-lg font-semibold text-white flex items-baseline gap-2">
      <span className="text-cyan-400 font-mono text-sm">{number}.</span>
      {title}
    </h2>
    <div className="mt-2 text-sm leading-relaxed text-zinc-300">{children}</div>
  </section>
);

const Callout = ({ children }) => (
  <div className="mt-3 px-3 py-2 rounded-lg border border-amber-700/40 bg-amber-950/30 text-xs sm:text-sm text-amber-200 flex items-start gap-2">
    <span className="text-base leading-none">👉</span>
    <span>{children}</span>
  </div>
);
